import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { canCancelBooking } from '@/lib/bookingCancel';

export default function ProviderCancelBooking({ booking, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [penalty, setPenalty] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [reasonCode, setReasonCode] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [loading, setLoading] = useState(false);

  const allowed = canCancelBooking(booking);
  const paid = booking?.payment?.status === 'paid' || booking?.status === 'confirmed';

  useEffect(() => {
    if (!open || !allowed) return;
    api.get(`/providers/me/bookings/${booking.id}/cancellation-preview`)
      .then(({ data }) => {
        if (!data.can_cancel) return;
        setPenalty(data.penalty || 0);
        setReasons(data.reasons || []);
      })
      .catch(() => toast.error('Could not load cancellation preview'));
  }, [open, booking?.id, allowed]);

  if (!allowed) return null;

  const confirm = async () => {
    const reason = reasonCode === 'other' ? otherReason.trim() : reasonCode;
    if (!reason) {
      toast.error('Please select or enter a reason');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/providers/me/bookings/${booking.id}/cancel`, {
        reason,
        reason_code: reasonCode === 'other' ? 'other' : reasonCode,
      });
      toast.success('Booking cancelled');
      setOpen(false);
      onCancelled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancellation failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        className="w-full border-red-200 text-red-700"
        data-testid="provider-cancel-booking-btn"
        onClick={() => setOpen(true)}
      >
        Cancel Booking
      </Button>
    );
  }

  return (
    <div className="resqly-card p-4 space-y-3 border border-red-100" data-testid="provider-cancel-dialog">
      <div className="font-semibold text-red-800">Cancel Booking</div>
      {!paid && <p className="text-sm text-slate-600">No penalty — customer has not paid.</p>}
      {paid && penalty > 0 && (
        <p className="text-sm text-amber-800" data-testid="provider-cancel-penalty">
          Provider penalty: ₹{penalty.toFixed(2)} will be deducted from your earnings.
        </p>
      )}
      <div className="space-y-2">
        {reasons.map((r) => (
          <label key={r} className="flex items-center gap-2 text-sm">
            <input type="radio" name="prov-cancel-reason" value={r} checked={reasonCode === r} onChange={() => setReasonCode(r)} />
            {r}
          </label>
        ))}
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="prov-cancel-reason" value="other" checked={reasonCode === 'other'} onChange={() => setReasonCode('other')} />
          Other
        </label>
        {reasonCode === 'other' && (
          <input className="w-full border rounded p-2 text-sm" value={otherReason} onChange={(e) => setOtherReason(e.target.value)} />
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Back</Button>
        <Button className="flex-1 bg-red-700 hover:bg-red-800" disabled={loading} data-testid="confirm-provider-cancel" onClick={confirm}>
          Confirm Cancellation
        </Button>
      </div>
    </div>
  );
}
